import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { router } from './router';
import { queryClient } from '@/lib/queryClient';
import { useUIStore } from '@/stores/uiStore';
import { useBranding } from '@/hooks/useBranding';
import { buildThemeFromBranding } from '@/utils/themeBuilder';
import { dateFnsLocale } from '@/utils/locale';
import { NotificationProvider } from '@/components/common/NotificationProvider';
import { DynamicFavicon } from '@/components/common/DynamicFavicon';
import { TestEnvironmentMarker } from '@/components/common/TestEnvironmentMarker';
import { useDeploymentEnvironment } from '@/hooks/useDeploymentEnvironment';
import { DynamicFontLoader } from '@/components/common/DynamicFontLoader';
import { PWAUpdatePrompt, OfflineIndicator, DynamicManifest } from '@/components/pwa';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import '@/lib/i18n';
import '@/styles/global.css';

export function App() {
  const { t } = useTranslation('common');
  const themeMode = useUIStore((s) => s.themeMode);
  const { branding, favicon, typography, colors } = useBranding();
  const brandedName = branding?.companyName;
  const { isTestEnvironment } = useDeploymentEnvironment();

  // Tab title: branded tenants get only the company name (no product suffix).
  // A test environment prefixes it so a test tab is never mistaken for
  // production (#3312).
  useEffect(() => {
    const title = brandedName ? t('tabTitle', { name: brandedName }) : t('appName');
    document.title = isTestEnvironment ? t('testEnvironment.tabTitle', { title }) : title;

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta && colors.primary?.main) {
      themeColorMeta.setAttribute('content', colors.primary.main);
    }

    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) {
      descMeta.setAttribute(
        'content',
        brandedName ? t('tabDescription', { name: brandedName }) : t('appName'),
      );
    }
  }, [brandedName, colors.primary?.main, isTestEnvironment, t]);

  // Build theme from branding configuration
  // Memoized to avoid unnecessary recreations
  const theme = useMemo(
    () => buildThemeFromBranding(branding, themeMode),
    [branding, themeMode]
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale()}>
            <CssBaseline />
            <DynamicFavicon url={favicon} />
            <DynamicFontLoader fontUrl={typography?.fontUrl} />
            <DynamicManifest />
            <NotificationProvider />
            {isTestEnvironment && <TestEnvironmentMarker onOpen={() => void router.navigate('/release')} />}
            <OfflineIndicator />
            <PWAUpdatePrompt />
            <RouterProvider router={router} />
          </LocalizationProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
